import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  FolderTree, Plus, Pencil, Trash2, ChevronRight, ChevronDown, Factory, Wrench,
} from 'lucide-react';
import {
  getVerticalCategoryTree, createVerticalCategory, updateVerticalCategory,
  deleteVerticalCategory,
} from '@/lib/api';

const VERTICAL_TABS = [
  { id: 'spare_parts', labelKey: 'admin.vert.spareParts', icon: Wrench, color: 'text-blue-600' },
  { id: 'machines', labelKey: 'admin.vert.machines', icon: Factory, color: 'text-purple-600' },
];

// Iterative flatten (avoids recursive React components which break the visual editor babel plugin)
function flattenTree(nodes, depth, collapsedSet, out) {
  for (const n of nodes) {
    const hasChildren = (n.children || []).length > 0;
    const isCollapsed = collapsedSet.has(n.node_id);
    out.push({ ...n, depth, hasChildren, isCollapsed });
    if (hasChildren && !isCollapsed) {
      flattenTree(n.children, depth + 1, collapsedSet, out);
    }
  }
}

// --- Category Form Dialog ---
const CategoryDialog = ({ open, onOpenChange, initialData, parentNodeId, vertical, onSubmit }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ node_name: '', sort_order: 0, active: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        node_name: initialData?.node_name || '',
        sort_order: initialData?.sort_order ?? 0,
        active: initialData?.active ?? true,
      });
    }
  }, [open, initialData]);

  const handleSubmit = async () => {
    if (!form.node_name.trim()) {
      toast.error(t('admin.vert.nameRequired'));
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        ...form,
        node_name: form.node_name.trim(),
        vertical,
        parent_node_id: initialData ? undefined : parentNodeId,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {initialData ? t('admin.vert.editCategory') : t('admin.vert.newCategory')}
          </DialogTitle>
          <DialogDescription>
            {parentNodeId && !initialData ? t('admin.vert.subCategoryCreate') : t('admin.vert.rootCategoryCreate')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{t('admin.vert.newCategory')} *</Label>
            <Input
              value={form.node_name}
              onChange={e => setForm(f => ({ ...f, node_name: e.target.value }))}
              data-testid="vertical-cat-name-input"
            />
          </div>
          <div>
            <Label>{t('admin.units.sortOrder')}</Label>
            <Input
              type="number"
              value={form.sort_order}
              onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value, 10) || 0 }))}
              data-testid="vertical-cat-sort-input"
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>{t('admin.units.active')}</Label>
            <Switch
              checked={form.active}
              onCheckedChange={v => setForm(f => ({ ...f, active: v }))}
              data-testid="vertical-cat-active-switch"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="vertical-cat-save-btn">
            {saving ? t('admin.units.saving') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --- Main Tab ---
export const VerticalCategoriesTab = () => {
  const { t } = useTranslation();
  const [activeVertical, setActiveVertical] = useState('spare_parts');
  const [tree, setTree] = useState([]);
  const [collapsed, setCollapsed] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [dialogState, setDialogState] = useState({ open: false, editData: null, parentNodeId: null });

  const loadTree = useCallback(async (vertical) => {
    setLoading(true);
    try {
      const res = await getVerticalCategoryTree(vertical, false);
      setTree(res.data || []);
      setCollapsed(new Set());
    } catch (err) {
      toast.error(t('admin.vert.loadTreeError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadTree(activeVertical);
  }, [activeVertical, loadTree]);

  const flatItems = useMemo(() => {
    const out = [];
    flattenTree(tree, 0, collapsed, out);
    return out;
  }, [tree, collapsed]);

  const toggleCollapse = (nodeId) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const handleSubmit = async (data) => {
    try {
      if (dialogState.editData) {
        await updateVerticalCategory(dialogState.editData.node_id, {
          node_name: data.node_name,
          sort_order: data.sort_order,
          active: data.active,
        });
        toast.success(t('admin.vert.categoryUpdated'));
      } else {
        await createVerticalCategory(data);
        toast.success(t('admin.vert.categoryCreated'));
      }
      loadTree(activeVertical);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.common.saveError'));
      throw err;
    }
  };

  const handleDelete = async (node) => {
    if (!window.confirm(t('admin.vert.confirmDelete', { name: node.node_name }))) return;
    try {
      await deleteVerticalCategory(node.node_id);
      toast.success(t('admin.vert.categoryDeactivated'));
      loadTree(activeVertical);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.machMfr.deactivateError'));
    }
  };

  return (
    <Card data-testid="vertical-categories-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FolderTree className="h-5 w-5 text-orange-500" />
          {t('admin.vert.title')}
        </CardTitle>
        <CardDescription>
          {t('admin.vert.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Vertical switcher */}
        <div className="flex items-center gap-2 mb-4" data-testid="vertical-switcher">
          {VERTICAL_TABS.map((v) => {
            const Icon = v.icon;
            const isActive = activeVertical === v.id;
            return (
              <Button
                key={v.id}
                variant={isActive ? 'default' : 'outline'}
                onClick={() => setActiveVertical(v.id)}
                className="gap-2"
                data-testid={`vertical-switch-${v.id}`}
              >
                <Icon className={`h-4 w-4 ${isActive ? '' : v.color}`} />
                {t(v.labelKey)}
              </Button>
            );
          })}
          <div className="flex-1" />
          <Button
            onClick={() => setDialogState({ open: true, editData: null, parentNodeId: null })}
            className="gap-1.5"
            data-testid="vertical-cat-add-root-btn"
          >
            <Plus className="h-4 w-4" /> {t('admin.vert.addRoot')}
          </Button>
        </div>

        {/* Flat tree */}
        {loading ? (
          <div className="py-12 text-center text-muted-foreground">{t('admin.vert.loading')}</div>
        ) : flatItems.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">
            {t('admin.vert.noCategoriesHint')}
          </div>
        ) : (
          <div className="border rounded-md divide-y" data-testid="vertical-cat-tree">
            {flatItems.map((item) => (
              <div
                key={item.node_id}
                className="flex items-center justify-between gap-3 py-2 px-3 hover:bg-muted/50 group"
                style={{ paddingLeft: `${item.depth * 20 + 12}px` }}
                data-testid={`vertical-cat-row-${item.node_id}`}
              >
                <button
                  onClick={() => item.hasChildren && toggleCollapse(item.node_id)}
                  className="flex items-center gap-2 flex-1 text-left"
                  disabled={!item.hasChildren}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    {item.hasChildren && !item.isCollapsed && <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                    {item.hasChildren && item.isCollapsed && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  </span>
                  <span className={`text-sm ${item.active ? '' : 'line-through text-muted-foreground'}`}>
                    {item.node_name}
                  </span>
                  {!item.active && <Badge variant="secondary" className="text-xs">{t('admin.units.inactive')}</Badge>}
                  {item.depth === 0 && item.hasChildren && (
                    <Badge variant="outline" className="text-xs">{(item.children || []).length}</Badge>
                  )}
                </button>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {item.depth < 2 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDialogState({ open: true, editData: null, parentNodeId: item.node_id })}
                      data-testid={`vertical-cat-add-child-${item.node_id}`}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDialogState({ open: true, editData: item, parentNodeId: null })}
                    data-testid={`vertical-cat-edit-${item.node_id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(item)}
                    data-testid={`vertical-cat-delete-${item.node_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <CategoryDialog
        open={dialogState.open}
        onOpenChange={(open) => setDialogState(s => ({ ...s, open }))}
        initialData={dialogState.editData}
        parentNodeId={dialogState.parentNodeId}
        vertical={activeVertical}
        onSubmit={handleSubmit}
      />
    </Card>
  );
};
